import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import * as Icon from 'react-feather';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { NavItem, NavLink } from 'reactstrap';
import {
  setIsCallWaiterModalOpen,
  setIsListOrdersModalOpen,
  setIsMoreSettingsModalOpen,
  setIsSetFiltersModalOpen,
} from '../redux/modal/modal.actions';
import PropTypes from 'prop-types';
import { setShowAppOverlay } from '../redux/customizer/customizer.actions';
import TagManager from 'react-gtm-module/dist/TagManager';
import { isAdminMode } from '../utility/util/util.service';
import AutocompleteField from '../components/@servierbar/form/fields/AutocompleteField';

const NavbarUser = ({
                      me,
                      store,
                      menuEntriesData,
                      isSearchOpen,
                      menuEntries,
                      kiosk,
                      table,
                      setIsListOrdersModalOpen,
                      isSetFiltersModalOpen,
                      setIsSetFiltersModalOpen,
                      foodAllergensFilter,
                      foodPreferencesFilter,
                      foodIngredientsFilter,
                      setIsMoreSettingsModalOpen,
                      setShowAppOverlay,
                      setIsSearchOpen,
                      setIsCallWaiterModalOpen,
                    }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef(null);

  const areAnyFiltersSet =
    foodAllergensFilter?.length > 0 ||
    foodPreferencesFilter?.length > 0 ||
    foodIngredientsFilter?.length > 0;

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const filterUniqueObjects = (array, property) => {
    const seen = new Set();
    return array.filter((obj) => {
      const value = obj[property];
      return seen.has(value) ? false : seen.add(value);
    });
  };

  const handleSearch = (e) => {
    const value = e?.target?.value || '';
    const searchFor = value.toLowerCase();
    setSearchValue(value);

    const filteredSuggestions = menuEntriesData?.filter((data) =>
      data.searchString.toLowerCase().includes(searchFor),
    ) || [];

    const suggestionMap = new Map();

    filteredSuggestions.forEach((suggestion) => {
      const matchingMenuEntries = menuEntries?.filter(
        (menuEntry) =>
          Array.isArray(menuEntry?.itemEntry?.data) &&
          menuEntry.itemEntry.data.some(
            (itemData) => itemData?.id === suggestion?.id,
          ),
      );

      const categories = matchingMenuEntries?.reduce((acc, menuEntry) => {
        return acc.concat(menuEntry?.itemEntry?.categories || []);
      }, []) || [];

      const uniqueCategories = [...new Set(categories)];

      if (suggestionMap.has(suggestion.id)) {
        const existingSuggestion = suggestionMap.get(suggestion.id);
        existingSuggestion.categories = [...new Set([...uniqueCategories])];
      } else {
        suggestionMap.set(suggestion.id, {
          ...suggestion,
          categories: uniqueCategories,
        });
      }
    });

    const mergedSuggestions = Array.from(suggestionMap.values());
    setSuggestions(
      mergedSuggestions.map((suggestion) => ({
        ...suggestion,
        categories: filterUniqueObjects(suggestion?.categories, 'id'),
      })),
    );

    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'InitSearch',
          customerProfileId: me?.requester?.customerProfile?.id,
          searchText: searchFor,
        },
      });
    }, 1500);
  };

  const handleSearchToggle = (e) => {
    e.stopPropagation();
    setIsSearchOpen(prev => !prev); // Use functional update

    // Reset search when closing
    if (isSearchOpen) {
      setSearchValue('');
      setSuggestions([]);
    }
  };

  return (
    <ul className="nav navbar-nav navbar-nav-user float-right disable-dbl-tap-zoom rounded-bottom">
      {store && menuEntriesData && (
        <>
          {!isAdminMode(me, store?.slug) && !kiosk && (
            <NavItem
              className="p-0 d-flex align-items-center"
              onClick={() => setIsListOrdersModalOpen(true)}
            >
              <NavLink className={classnames('nav-link-search', 'mr-1')}>
                <Icon.ShoppingCart size={21} data-testid="ordersShortcut" />
              </NavLink>
            </NavItem>
          )}
          {table &&
            store?.merchantProfile?.posIntegration?.enableCallAWaiter &&
            store?.merchantProfile?.posIntegration?.dummyItemRefs?.length > 0 && (
              <NavItem
                className="p-0 d-flex align-items-center"
                onClick={() => setIsCallWaiterModalOpen(true)}
              >
                <NavLink className={classnames('nav-link-search', 'mr-1')}>
                  <Icon.Bell size={21} id="filter" data-tour="filter" />
                </NavLink>
              </NavItem>
            )}
          <NavItem className="nav-search p-0 d-flex align-items-center">
            <NavLink
              className={classnames('nav-link-search', 'mr-1')}
              onClick={handleSearchToggle}
            >
              <Icon.Search size={21} data-tour="search" />
            </NavLink>
            <div
              className={classnames('search-input', {
                open: isSearchOpen,
                'd-none': !isSearchOpen,
              })}
            >
              <div className="search-input-icon">
                <Icon.Search size={17} className="primary" onClick />
              </div>
              <FormattedMessage id="searchPlaceholder">
                {(placeholder) => (
                  <AutocompleteField
                    ref={searchInputRef}
                    className="form-control"
                    suggestions={suggestions}
                    placeholder={placeholder}
                    autoFocus={isSearchOpen}
                    value={searchValue}
                    onChange={handleSearch}
                    onKeyDown={(e) => {
                      if (e.keyCode === 27 || e.keyCode === 13) {
                        setShowAppOverlay(false);
                        setIsSearchOpen(false);
                      }
                    }}
                    onBlur={() => {
                      if (searchValue.length === 0) {
                        setIsSearchOpen(false);
                      }
                    }}
                    onSuggestionsShown={(userInput) => {
                      if (isSearchOpen) {
                        setShowAppOverlay(!!userInput);
                      }
                    }}
                    onSuggestionItemClick={() => {
                      setShowAppOverlay(false);
                      setIsSearchOpen(false);
                      setSearchValue('');
                    }}
                    externalClick={() => {
                      setShowAppOverlay(false);
                      setIsSearchOpen(false);
                    }}
                  />
                )}
              </FormattedMessage>
              <div className="search-input-close">
                <Icon.X
                  size={24}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAppOverlay(false);
                    setIsSearchOpen(false);
                    setSearchValue('');
                  }}
                />
              </div>
            </div>
          </NavItem>

          <NavItem
            className="p-0 d-flex align-items-center"
            onClick={() => setIsSetFiltersModalOpen(true)}
          >
            <NavLink
              className={classnames('nav-link-search', 'mr-1', {
                'nav-link-filter': areAnyFiltersSet,
              })}
            >
              <Icon.Sliders
                size={21}
                id="filter"
                data-tour="filter"
                className={classnames({ primary: areAnyFiltersSet })}
              />
            </NavLink>
          </NavItem>
        </>
      )}

      <NavItem
        className="p-0 d-flex align-items-center"
        onClick={() => setIsMoreSettingsModalOpen(true)}
      >
        <NavLink className={classnames('nav-link-search', 'mr-1')}>
          <Icon.MoreVertical
            data-testid="moreSettingsButton"
            size={21}
            data-tour="more-settings"
          />
        </NavLink>
      </NavItem>
    </ul>
  );
};

const mapStateToProps = (state) => ({
  menuEntriesData: state.store.menuEntriesData,
  isSetFiltersModalOpen: state.modals.isSetFiltersModalOpen,
  foodAllergensFilter: state.me.foodAllergensFilter,
  foodPreferencesFilter: state.me.foodPreferencesFilter,
  foodIngredientsFilter: state.me.foodIngredientsFilter,
  pickupOrder: state.me.pickupOrder,
  menuEntries: state.store.menuEntries,
  kiosk: state.store.kiosk,
  table: state.store.table,
});

export default connect(mapStateToProps, {
  setIsCallWaiterModalOpen,
  setIsSetFiltersModalOpen,
  setIsListOrdersModalOpen,
  setIsMoreSettingsModalOpen,
  setShowAppOverlay,
})(NavbarUser);